import type { FacilityListParams } from '@gyym/sdk'
import { useGyymClient, useGyymSWR } from '@gyym/sdk-react'
import React from 'react'

export type UseFacilitiesConfig = {
  revalidateOnFocus?: boolean
  revalidateOnMount?: boolean
  revalidateOnReconnect?: boolean
  focusThrottleInterval?: number
}

export const getFacilitiesSWRConfig = (config?: UseFacilitiesConfig) => ({
  // 検索結果はカタログ性のデータで、検索操作のたびに再取得される。
  // タブ/アプリ復帰ごとの再検索を抑えるため focus 再検証をスロットルする
  // (off にはしない: グローバルの revalidateOnMount/IfStale: false と
  // 組み合わさるとセッション内で stale なまま固定されるため)
  focusThrottleInterval: 10 * 60 * 1000,
  // reconnect は focus のスロットルとは別イベントで即時再検証されるため、
  // 検索操作か focus 再検証に更新を一本化する
  revalidateOnReconnect: false,
  ...config,
})

export const useFacilities = (
  params: FacilityListParams,
  shouldFetch = true,
  config?: UseFacilitiesConfig,
) => {
  const client = useGyymClient()
  const { data, ...rest } = useGyymSWR(
    shouldFetch ? [client.user.facilities.list, params] : null,
    getFacilitiesSWRConfig(config),
  )

  const facilities = React.useMemo(
    () =>
      data
        ? data.data.map((f) => ({
            ...f,
            // Convert to shorthand
            lat: f.latitude,
            lng: f.longitude,
          }))
        : undefined,
    [data],
  )

  return {
    data: data
      ? {
          data: facilities,
          meta: data?.meta,
        }
      : undefined,
    ...rest,
  }
}
